import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { CreateLibraryCommand } from '@model/library/commands/library-create-command';
import {
  LibraryBackendMapper,
  CreateLibraryBackendRequest
} from '@model/library/mappers/library-backend-mapper';
// eslint-disable-next-line deprecation/deprecation
import { LibraryLegacyAdapter } from '@model/library/mappers/library-legacy-adapter';
import {
  WorkListMapper,
  CreateLibraryBackendResponse
} from './worklist.mapper';
import { HttpErrorHandlerService } from '@core/error-handling/http-error-handler.service';
import { environment } from '@env/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  LibraryRepository,
  CreateLibraryResponse,
  CreateLibraryInput,
  RawCreateLibraryInput
} from '@service/laboratory/library/library-repository';

/**
 * Library Repository Adapter
 *
 * Implements the LibraryRepository interface defined in the domain layer.
 * Handles HTTP communication and data transformation between backend DTOs and domain models.
 *
 * Supports both:
 * - New typed CreateLibraryCommand (recommended)
 * - Legacy CreateLibraryRequest (deprecated, for backward compatibility)
 *
 * Architecture Layer: Infrastructure
 * - Implements domain interface (Dependency Inversion)
 * - Uses LibraryBackendMapper for command → backend request transformation
 */
@Injectable({
  providedIn: 'root'
})
export class LibraryRepositoryAdapter extends LibraryRepository {
  private readonly http = inject(HttpClient);
  private readonly BASE_URL = `${environment.laboratory.baseUrl}${environment.laboratory.context}`;
  private readonly errorHandler = inject(HttpErrorHandlerService);

  /**
   * Create a library using a typed command or legacy request
   *
   * @param input Command or legacy request with library data
   * @returns Observable with created library information
   */
  createLibrary(input: CreateLibraryInput): Observable<CreateLibraryResponse> {
    // Check if it's legacy microarray with multiple chips (batch creation)
    if (this.isLegacyMicroarrayBatchInput(input)) {
      console.warn(
        '[LibraryRepository] Creating multiple microarray libraries in batch mode'
      );
      // Send legacy format directly to backend
      return this.http
        .post<CreateLibraryBackendResponse>(
          `${this.BASE_URL}/library/create`,
          input
        )
        .pipe(
          map((response) => {
            if (!response) {
              throw new Error('No response from server');
            }
            return WorkListMapper.fromBackendLibraryResponse(response);
          }),
          catchError((error: HttpErrorResponse) =>
            this.errorHandler.handleError('createLibrary', error)
          )
        );
    }

    // Convert to command if legacy format
    const command = this.normalizeToCommand(input);

    // Transform command to backend format using the mapper
    const backendRequest: CreateLibraryBackendRequest =
      LibraryBackendMapper.toBackendRequest(command);

    return this.http
      .post<CreateLibraryBackendResponse>(
        `${this.BASE_URL}/library/create`,
        backendRequest
      )
      .pipe(
        map((response) => {
          if (!response) {
            throw new Error('No response from server');
          }
          return WorkListMapper.fromBackendLibraryResponse(response);
        }),
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('createLibrary', error)
        )
      );
  }

  /**
   * Normalizes input to a CreateLibraryCommand
   * If already a command, returns as-is
   * If legacy format, converts using the adapter
   */
  private normalizeToCommand(input: CreateLibraryInput): CreateLibraryCommand {
    // Check if it's already a command by looking for the technology enum value
    if (this.isCreateLibraryCommand(input)) {
      return input;
    }
    // Convert legacy format to command
    return LibraryLegacyAdapter.toCommand(input);
  }

  /**
   * Type guard to check if input is already a CreateLibraryCommand
   */
  private isCreateLibraryCommand(
    input: CreateLibraryInput
  ): input is CreateLibraryCommand {
    // Commands have 'specimens' array, legacy has 'samples' or 'libraryGroups'
    return 'specimens' in input && Array.isArray(input.specimens);
  }

  private isRawCreateLibraryInput(
    input: CreateLibraryInput
  ): input is RawCreateLibraryInput {
    return 'samples' in input || 'libraryGroups' in input;
  }

  private isLegacyMicroarrayBatchInput(
    input: CreateLibraryInput
  ): input is RawCreateLibraryInput & { libraryGroups: unknown[] } {
    return (
      this.isRawCreateLibraryInput(input) &&
      input.technology === 'MICROARREGLOS' &&
      Array.isArray(input.libraryGroups) &&
      input.libraryGroups.length > 1
    );
  }
}
